/**
 * PersistentAffine - Keeps Affine iframe mounted across navigation
 *
 * Follows PersistentChatwoot pattern:
 * - Lazy mount: only renders after user first visits /affine
 * - visibility:hidden when navigating away (keeps SharedWorker alive)
 * - Shown instantly when returning to /affine
 */

import { useLocation } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { AffineEmbed } from "@/components/AffineEmbed";
import { useAuthStore } from "@/lib/store/authStore";

export function PersistentAffine() {
  const location = useLocation();
  const isOnAffinePage = location.pathname === "/affine";

  // Lazy mount: only render after first visit
  const [hasVisitedAffine, setHasVisitedAffine] = useState(false);

  const isProvisioned = useAuthStore((state) => state.isProvisioned);

  useEffect(() => {
    if (isOnAffinePage && !hasVisitedAffine) {
      setHasVisitedAffine(true);
    }
  }, [isOnAffinePage, hasVisitedAffine]);

  const handleReady = useCallback(() => {
    console.log("[PersistentAffine] Ready");
  }, []);

  const handleError = useCallback((message: string) => {
    console.error("[PersistentAffine] Error:", message);
  }, []);

  // Don't render if never visited or not provisioned
  if (!hasVisitedAffine || !isProvisioned) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 top-0 z-10 md:left-[72px] md:right-[48px]"
      style={{
        visibility: isOnAffinePage ? "visible" : "hidden",
        pointerEvents: isOnAffinePage ? "auto" : "none",
      }}
    >
      <AffineEmbed
        className="h-full w-full"
        onReady={handleReady}
        onError={handleError}
      />
    </div>
  );
}
